import React, { useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Form, FormGroup, Label, Input, Button, Table, Col, Row, Card, CardBody, CardHeader, Container, Modal, ModalHeader, ModalBody, Alert, Spinner } from 'reactstrap';
import { useTranslation } from 'react-i18next';
import { invoiceActions } from '../../sagas/invoiceSlice';
import Swal from 'sweetalert2';
import { formatPrice, roundDocumentAmounts } from '../../helpers/utility';
import Select from "react-select";
import { clientActions } from '../../sagas/clientSlice';
import { productActions } from '../../sagas/productSlice';
import * as Yup from 'yup'; // Import Yup for validation
import { useFormik } from 'formik';
import { reglementActions } from '../../sagas/reglementSlice';
import CommandeLine from './CommandeLine';
import { FaArrowDown, FaArrowUp, FaBalanceScale, FaCalendarAlt, FaMoneyBillWave, FaShieldAlt } from 'react-icons/fa';
import axiosInstance from '../../services/api_helper';
const InvoiceForm = ({ initialValues, onClose }) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const [formState, setForm] = useState(initialValues);
    const { isLoading, invoiceNumber } = useSelector(state => state.invoices);
    const unites = useSelector(status => status.products.unites);
    const clients = useSelector(state => state.clients.clients);
    const products = useSelector(state => state.products.products);
    const [selectedClient, setSelectedClient] = useState(null);
    const { loading: loadingReglements, error: errorReglements, echeances, cautions } = useSelector((state) => state.reglements);
    const [showEcheanceModal, setShowEcheanceModal] = useState(false);
    const [showCautionModal, setShowCautionModal] = useState(false);
    const [availableAdvances, setAvailableAdvances] = useState([]);
    const [selectedAdvanceIds, setSelectedAdvanceIds] = useState([]);
    const [loadingAdvances, setLoadingAdvances] = useState(false);
    const isEditing = Boolean(initialValues?.id);

    const [filters, setFilters] = useState({
        clientId: null,
        dateFrom: null,
        dateTo: null,
        statut: "EN_ATTENTE"
    });

    useEffect(() => {
        if (!formState?.numero) {
            setForm(prevForm => ({ ...prevForm, numero: invoiceNumber }));
        }
        if (!isEditing) {
            dispatch(invoiceActions.fetchNewInvoiceNumber({companyId: formState?.companyId}));
        }

        if (!clients || clients.length === 0) {
            dispatch(clientActions.search({
                query: ""
            }));
        }

        if (!products || products.length === 0) {
            dispatch(productActions.findAll({
                search: "",
                page: 0,
                size: 10,
            }));
        }
    }, [invoiceNumber]);

    // Validation schema
    const validationSchema = Yup.object().shape({
        clientId: Yup.number()
            .required(t('bonLivraison.clientRequired')), // Ensure a client is selected
        lignes: Yup.array().min(1, t('bonLivraison.atLeastOneItem')), // Ensure at least one item is added

    });

    const formik = useFormik({
        initialValues: {
            ...formState,
        },
        validationSchema,
        enableReinitialize: true,
        onSubmit: (values) => {
            console.log(values)
            if (values.clientLivreId && Number(values.clientLivreId) !== Number(values.clientId) && !values.confirmDifferentBillingCustomer) {
                Swal.fire({
                    icon: 'warning',
                    title: 'Confirmation du client facturé requise',
                    text: 'Confirmez explicitement que la facture doit être établie au nom d’un client différent du client livré.',
                });
                return;
            }
            if (!values.dateEcheance) {
                Swal.fire({
                    icon: 'warning',
                    title: t('facture.validation.dueDateRequired'),
                    text: t('facture.validation.dueDateRequiredMessage'),
                });
                return;
            }

            if (!values.modeReglement) {
                Swal.fire({
                    icon: 'warning',
                    title: t('facture.validation.paymentMethodRequired'),
                    text: t('facture.validation.paymentMethodRequiredMessage'),
                });
                return;
            }

            const beforeTotal = Number(initialValues?.montantTotalTTC || 0);
            const beforePaid = Number(initialValues?.montantRegle || 0);
            const afterTotal = Number(values.montantTotalTTC || 0);
            const paidAfterUpdate = Math.min(beforePaid, afterTotal);
            const released = Math.max(0, beforePaid - paidAfterUpdate);
            Swal.fire({
                title: isEditing ? 'Confirmer la modification' : 'Créer et confirmer la facture',
                html: isEditing
                    ? `<div class="text-start"><p>La facture <b>${values.numero || ''}</b> sera mise à jour.</p><table class="table table-sm"><thead><tr><th></th><th>Avant</th><th>Après</th></tr></thead><tbody><tr><td>Total TTC</td><td>${formatPrice(beforeTotal)}</td><td>${formatPrice(afterTotal)}</td></tr><tr><td>Règlements affectés</td><td>${formatPrice(beforePaid)}</td><td>${formatPrice(paidAfterUpdate)}</td></tr><tr><td>Reste à payer</td><td>${formatPrice(Math.max(0, beforeTotal - beforePaid))}</td><td>${formatPrice(Math.max(0, afterTotal - paidAfterUpdate))}</td></tr></tbody></table>${released > 0 ? `<p class="text-warning">${formatPrice(released)} seront restitués au BL source ou rendus disponibles comme avance.</p>` : ''}<p class="small text-muted mb-0">Les quantités, retours, allocations et soldes seront recalculés par le serveur.</p></div>`
                    : `<div class="text-start"><p>Vérifiez les éléments avant création.</p><ul><li>Client facturé : <b>${clientOptions.find(option => Number(option.value) === Number(values.clientId))?.label || '-'}</b></li><li>BL sélectionnés : <b>${values.deliveryMappings?.length || 0}</b></li><li>Lignes à facturer : <b>${values.lignes?.length || 0}</b></li><li>Total TTC : <b>${formatPrice(values.montantTotalTTC || 0)}</b></li><li>Règlements BL transférés : <b>${formatPrice(paymentPreview.covered)}</b></li><li>Reste attendu : <b>${formatPrice(paymentPreview.remaining)}</b></li><li>Statut attendu : <b>${paymentPreview.status}</b></li></ul><p class="small text-muted mb-0">Le serveur revalidera les quantités, retours et allocations dans la même transaction.</p></div>`,
                icon: 'question',
                showCancelButton: true,
                confirmButtonText: isEditing ? 'Enregistrer les modifications' : 'Créer et confirmer la facture',
                cancelButtonText: 'Revenir au formulaire',
            }).then((result) => {
                if (result.isConfirmed) {
                    const invoiceData = roundDocumentAmounts({
                        ...values,
                        transfererPaiementsBL: !isEditing,
                        paymentAllocations: isEditing ? undefined : selectedAdvanceAllocations,
                        modeReglement: values.modeReglement === "-1" ? null : values.modeReglement,
                        statut: isEditing ? values.statut : 'IMPAYE'
                    });
                    const onSuccess = () => {
                        Swal.fire({
                            icon: 'success',
                            title: isEditing ? t('facture.updateSuccess') : t('facture.invoiceSaved'),
                            text: isEditing ? t('facture.updateSuccessMessage') : t('facture.invoiceSavedMessage'),
                        });
                        formik.resetForm();
                        onClose();
                    };
                    if (isEditing) {
                        dispatch(invoiceActions.updateInvoiceRequest({
                            id: initialValues.id,
                            invoiceData,
                            onSuccess,
                            onError: (error) => Swal.fire({
                                icon: 'error',
                                title: t('facture.updateError'),
                                text: error?.response?.data?.message || error?.message || t('facture.updateErrorMessage'),
                            }),
                        }));
                    } else {
                        dispatch(invoiceActions.addInvoice({
                            invoice: invoiceData,
                            onSuccess,
                        }));
                    }
                }
            });
        }
    });

    const { values, getFieldProps, handleSubmit, setFieldValue, errors, touched } = formik;

    const clientOptions = clients?.map(client => ({
        solde: client.solde,
        totalDebit: client.totalDebit,
        totalCredit: client.totalCredit,
        value: client.id,
        label: client.typeClient === 'PARTICULIER'
            ? `${client.nom} ${client.prenom}`
            : client.entreprise,
    })) ?? [];

    // Calculate totals
    const montantTotalTaxe = values.montantTotalTaxe || 0;
    const montantTotalTTC = values.montantTotalTTC || 0;
    const montantTotalHT = values.montantTotalHT || 0
    const sourceDocumentNumbers = useMemo(() => {
        const fromLines = (Array.isArray(values.lignes) ? values.lignes : [])
            .map(line => line.sourceBonLivraisonNumero)
            .filter(Boolean);
        const fromMappings = (Array.isArray(values.deliveryMappings) ? values.deliveryMappings : [])
            .map(delivery => delivery.numero)
            .filter(Boolean);
        const fromReferences = String(values.bonLivraionsRefs || '')
            .split(',')
            .map(reference => reference.trim())
            .filter(Boolean);
        return [...new Set([...fromMappings, ...fromLines, ...fromReferences])];
    }, [values.bonLivraionsRefs, values.deliveryMappings, values.lignes]);

    const paymentPreview = useMemo(() => {
        if (isEditing) {
            const total = Number(values.montantTotalTTC || 0);
            const covered = Math.min(total, Number(initialValues?.montantRegle || 0));
            const remaining = Math.max(0, total - covered);
            return {
                rows: [],
                covered,
                advanceCovered: 0,
                remaining,
                unused: 0,
                status: covered <= 0 ? 'Impayee' : remaining <= 0 ? 'Reglee' : 'Partiellement reglee',
            };
        }
        let invoiceRemaining = Number(values.montantTotalTTC || 0);
        const rows = (Array.isArray(values.deliveryMappings) ? values.deliveryMappings : []).map(delivery => {
            const contribution = (Array.isArray(values.lignes) ? values.lignes : [])
                .filter(line => Number(line.sourceBonLivraisonId) === Number(delivery.id))
                .reduce((sum, line) => sum + Number(line.montantTTC || 0), 0);
            const available = Number(delivery.montantPaiementAssocieBL ?? delivery.montantPaye ?? 0);
            const proposed = Math.max(0, Math.min(available, contribution, invoiceRemaining));
            invoiceRemaining = Math.max(0, invoiceRemaining - proposed);
            return { ...delivery, contribution, available, proposed };
        });
        const covered = rows.reduce((sum, row) => sum + row.proposed, 0);
        const total = Number(values.montantTotalTTC || 0);
        const advanceAvailable = availableAdvances
            .filter(advance => selectedAdvanceIds.includes(advance.id))
            .reduce((sum, advance) => sum + Number(advance.montantDisponible || 0), 0);
        const advanceCovered = Math.min(Math.max(0, total - covered), advanceAvailable);
        const remaining = Math.max(0, total - covered - advanceCovered);
        return {
            rows,
            covered,
            advanceCovered,
            remaining,
            unused: rows.reduce((sum, row) => sum + Math.max(0, row.available - row.proposed), 0),
            status: covered + advanceCovered <= 0 ? 'Impayee' : remaining <= 0 ? 'Reglee' : 'Partiellement reglee',
        };
    }, [isEditing, initialValues?.montantRegle, values.deliveryMappings, values.lignes, values.montantTotalTTC, availableAdvances, selectedAdvanceIds]);

    useEffect(() => {
        if (isEditing || !values.clientId || !values.companyId) {
            setAvailableAdvances([]);
            setSelectedAdvanceIds([]);
            return;
        }
        let cancelled = false;
        setLoadingAdvances(true);
        axiosInstance.get(`/api/avances/client/${values.clientId}/disponibles`, {
            params: { companyId: values.companyId }
        }).then(({ data }) => {
            if (!cancelled) setAvailableAdvances(Array.isArray(data) ? data : []);
        }).catch(() => {
            if (!cancelled) setAvailableAdvances([]);
        }).finally(() => {
            if (!cancelled) setLoadingAdvances(false);
        });
        return () => { cancelled = true; };
    }, [isEditing, values.clientId, values.companyId]);

    const selectedAdvanceAllocations = useMemo(() => {
        let remaining = Math.max(0, Number(values.montantTotalTTC || 0) - paymentPreview.covered);
        const allocations = {};
        availableAdvances.filter(advance => selectedAdvanceIds.includes(advance.id)).forEach(advance => {
            const amount = Math.min(remaining, Number(advance.montantDisponible || 0));
            if (amount > 0 && advance.reglementId) allocations[advance.reglementId] = amount;
            remaining = Math.max(0, remaining - amount);
        });
        return allocations;
    }, [availableAdvances, selectedAdvanceIds, values.montantTotalTTC, paymentPreview.covered]);

    const saveDraft = () => {
        if (!values.clientId || !Array.isArray(values.lignes) || values.lignes.length === 0) {
            Swal.fire({ icon: 'warning', title: 'Brouillon incomplet', text: 'Selectionnez un client et au moins une ligne.' });
            return;
        }
        dispatch(invoiceActions.addInvoice({
            invoice: { ...values, paymentAllocations: {}, statut: 'BROUILLON' },
            onSuccess: () => {
                Swal.fire({ icon: 'success', title: 'Brouillon enregistre' });
                formik.resetForm();
                onClose();
            }
        }));
    };

    const getTotalMontant = (items) => {
        return items.reduce((total, item) => total + item.montantRegle, 0);
    };

    return (
        <Container fluid>
            <Form onSubmit={handleSubmit} onAbort={onClose}>
                <div className="d-flex justify-content-between align-items-start flex-wrap gap-2 mb-3">
                    <div><h3 className="mb-1">{isEditing ? 'Modifier la facture' : 'Nouvelle facture'}</h3><div className="text-muted">Vérifiez les informations, les BL et les quantités sur une seule page.</div></div>
                    <Button type="button" color="light" onClick={onClose}>Retour</Button>
                </div>
                <Card className="shadow-sm border-0 mb-3">
                    <CardHeader className="bg-white border-0 pt-3"><h5 className="mb-3">Informations essentielles</h5>
                        <Row>
                            <Col md="3">
                                <FormGroup>
                                    <Label>{t('facture.invoiceNumber')}</Label>
                                    <Input
                                        type="text"
                                        value={values.numero || ''}
                                        disabled
                                    />
                                </FormGroup>
                            </Col>
                            <Col md="3">
                                <Label htmlFor="clientId">Client facture:</Label>
                                <Select
                                    id="clientId"
                                    options={clientOptions}
                                    value={clientOptions.find(option => option.value === values.clientId)}
                                    onChange={(selectedOption) => {
                                        setFieldValue('clientId', selectedOption.value);
                                        setFieldValue('confirmDifferentBillingCustomer', false);
                                        setSelectedClient(selectedOption);
                                        dispatch(reglementActions.echeanceClient({ ...filters, clientId: selectedOption.value }));
                                        dispatch(reglementActions.cautionClient({ ...filters, clientId: selectedOption.value }));
                                        setFilters(f => ({ ...f, clientId: selectedOption.value }));
                                    }}
                                    placeholder={t('bonLivraison.selectClient')}
                                    isSearchable
                                    menuPortalTarget={typeof window !== 'undefined' ? document.body : null}
                                    styles={{
                                        menuPortal: base => ({ ...base, zIndex: 9999 })
                                    }}
                                />
                                {errors.clientId && touched.clientId && (
                                    <div className="text-danger small mt-1">{errors.clientId}</div>
                                )}
                                {values.clientLivreId && (
                                    <div className="text-muted small mt-1">
                                        Client livre : {values.clientNom || clientOptions.find(option => Number(option.value) === Number(values.clientLivreId))?.label || values.clientLivreId}
                                    </div>
                                )}
                            </Col>
                            <Col md="3">
                                <FormGroup>
                                    <Label>{t('facture.invoiceDate')}</Label>
                                    <Input
                                        type="date"
                                        name="dateFacture"
                                        {...getFieldProps('dateFacture')}
                                    />
                                </FormGroup>
                            </Col>
                            <Col md="3">
                                <Label>{t('facture.dueDate')}</Label>
                                <Input
                                    type="date"
                                    id="dateEcheance"
                                    {...getFieldProps('dateEcheance')}
                                    className="form-control"
                                />
                            </Col>
                        </Row>
                        {values.clientLivreId && Number(values.clientLivreId) !== Number(values.clientId) && (
                            <Alert color="warning" className="py-2">
                                <div className="mb-2">
                                    Les marchandises ont été livrées à <strong>{values.clientNom || values.clientLivreId}</strong>, mais la facture sera établie au nom de <strong>{clientOptions.find(option => Number(option.value) === Number(values.clientId))?.label || values.clientId}</strong>.
                                </div>
                                <FormGroup check className="mb-0">
                                    <Input
                                        id="confirmDifferentBillingCustomer"
                                        type="checkbox"
                                        checked={Boolean(values.confirmDifferentBillingCustomer)}
                                        onChange={event => setFieldValue('confirmDifferentBillingCustomer', event.target.checked)}
                                    />
                                    <Label check htmlFor="confirmDifferentBillingCustomer">Je confirme ce client facturé différent.</Label>
                                </FormGroup>
                            </Alert>
                        )}
                        <Row>
                            <Col md={3}>
                                <FormGroup>
                                    <Label for="modeReglement">
                                        <FaMoneyBillWave className="me-1" />
                                        {t('facture.modeReglement')} *
                                    </Label>
                                    <Input
                                        type="select"
                                        id="modeReglement"
                                        name="modeReglement"
                                        {...getFieldProps('modeReglement')}
                                    >
                                        <option value="">{t('common.select')}</option>
                                        <option value="ESPECES">{t('facture.modeReglement.espece')}</option>
                                        <option value="CHEQUE">{t('facture.modeReglement.cheque')}</option>
                                        <option value="VIREMENT">{t('facture.modeReglement.virement')}</option>
                                        <option value="CARTE_BANCAIRE">{t('facture.modeReglement.card')}</option>
                                        <option value="TRAITE">{t('facture.modeReglement.traite')}</option>
                                    </Input>
                                </FormGroup>
                            </Col>
                        </Row>
                        {selectedClient && (
                            <Row className="mt-2">
                                <Col>
                                    <Row className="g-3 align-items-stretch">
                                        <Col xs={12} md={2}>
                                            <Card className="shadow-sm h-100 bg-gradient bg-light border-0">
                                                <CardBody className="d-flex flex-column justify-content-center align-items-center py-3">
                                                    <FaBalanceScale className="mb-2 text-success" size={28} />
                                                    <span className="text-muted text-uppercase small mb-1">{t('client.balance')}</span>
                                                    {selectedClient.totalCredit > selectedClient.totalDebit ? (
                                                        <span className="fw-bold fs-5 text-success">+{formatPrice(Math.abs(selectedClient.solde))}</span>
                                                    ) : selectedClient.totalCredit < selectedClient.totalDebit ? (
                                                        <span className="fw-bold fs-5 text-danger">-{formatPrice(Math.abs(selectedClient.solde))}</span>
                                                    ) : (
                                                        <span className="fw-bold fs-5 text-muted">{formatPrice(0)}</span>
                                                    )}
                                                </CardBody>
                                            </Card>
                                        </Col>
                                        <Col xs={12} md={2}>
                                            <Card className="shadow-sm h-100 bg-gradient bg-light border-0">
                                                <CardBody className="d-flex flex-column justify-content-center align-items-center py-3">
                                                    <FaArrowDown className="mb-2 text-danger" size={28} />
                                                    <span className="text-muted text-uppercase small mb-1">{t('client.totalDebit')}</span>
                                                    <span className="fw-bold fs-5">{formatPrice(selectedClient.totalDebit)}</span>
                                                </CardBody>
                                            </Card>
                                        </Col>
                                        <Col xs={12} md={2}>
                                            <Card className="shadow-sm h-100 bg-gradient bg-light border-0">
                                                <CardBody className="d-flex flex-column justify-content-center align-items-center py-3">
                                                    <FaArrowUp className="mb-2 text-primary" size={28} />
                                                    <span className="text-muted text-uppercase small mb-1">{t('client.totalCredit')}</span>
                                                    <span className="fw-bold fs-5">{formatPrice(selectedClient.totalCredit)}</span>
                                                </CardBody>
                                            </Card>
                                        </Col>
                                        <Col xs={12} md={3}>
                                            <Card className="shadow-sm h-100 bg-gradient bg-warning bg-opacity-10 border-0">
                                                <CardBody className="d-flex flex-column justify-content-center align-items-center py-3">
                                                    <FaCalendarAlt className="mb-2 text-warning" size={28} />
                                                    <span className="text-muted text-uppercase small mb-1">{t('client.echeances')}</span>
                                                    <span className="fw-bold fs-5 text-warning">{echeances?.length || 0}</span>
                                                    <Button color="link" size="sm" className="mt-2 px-2 py-0" onClick={() => setShowEcheanceModal(true)}>
                                                        {t('client.viewDetails')}
                                                    </Button>
                                                </CardBody>
                                            </Card>
                                        </Col>
                                        <Col xs={12} md={3}>
                                            <Card className="shadow-sm h-100 bg-gradient bg-info bg-opacity-10 border-0">
                                                <CardBody className="d-flex flex-column justify-content-center align-items-center py-3">
                                                    <FaShieldAlt className="mb-2 text-info" size={28} />
                                                    <span className="text-muted text-uppercase small mb-1">{t('client.cautions')}</span>
                                                    <span className="fw-bold fs-5 text-info">{cautions?.length || 0}</span>
                                                    <Button color="link" size="sm" className="mt-2 px-2 py-0" onClick={() => setShowCautionModal(true)}>
                                                        {t('client.viewDetails')}
                                                    </Button>
                                                </CardBody>
                                            </Card>
                                        </Col>
                                    </Row>
                                </Col>
                            </Row>
                        )}
                    </CardHeader>
                    <CardBody className="pt-0">
                            <div className="border-top pt-3 mb-4">
                                <div className="d-flex justify-content-between align-items-center mb-3">
                                    <div><h5 className="mb-1">Bons de livraison selectionnes</h5><div className="small text-muted">Le serveur reverifiera la disponibilite lors de la confirmation.</div></div>
                                    <span className="badge bg-primary">{sourceDocumentNumbers.length} BL</span>
                                </div>
                                {isEditing && sourceDocumentNumbers.length > 0 ? (
                                    <div className="d-flex flex-wrap gap-2">
                                        {sourceDocumentNumbers.map(numero => <span key={numero} className="badge bg-light text-dark border fs-6">{numero}</span>)}
                                    </div>
                                ) : Array.isArray(values.deliveryMappings) && values.deliveryMappings.length > 0 ? (
                                    <div className="table-responsive"><Table hover className="align-middle">
                                        <thead><tr><th>BL</th><th>Situation commerciale</th><th className="text-end">Montant net</th><th className="text-end">Paiements lies</th><th className="text-end">Reste</th></tr></thead>
                                        <tbody>{values.deliveryMappings.map(delivery => {
                                            const initial = Number(delivery.montantTotalTTC || 0);
                                            const returned = Number(delivery.montantRetourne || 0);
                                            const net = Number(delivery.montantNet ?? Math.max(0, initial - returned));
                                            const paid = Number(delivery.montantPaye || 0);
                                            return <tr key={delivery.id}>
                                                <td><strong>{delivery.numero}</strong><div className="small text-muted">{delivery.dateLivraison || '-'}</div></td>
                                                <td><div className="small">Livre {formatPrice(initial)} - retours {formatPrice(returned)}</div><div className="small text-muted">{delivery.statutFacturation || 'NON_FACTURE'}</div></td>
                                                <td className="text-end fw-bold">{formatPrice(net)}</td><td className="text-end text-success">{formatPrice(paid)}</td><td className="text-end">{formatPrice(Math.max(0, net - paid))}</td>
                                            </tr>;
                                        })}</tbody>
                                    </Table></div>
                                ) : <Alert color="secondary">Facture directe sans bon de livraison source.</Alert>}
                            </div>
                        {paymentPreview.rows.length > 0 && (
                            <Card className="border-primary-subtle bg-primary bg-opacity-10 mb-4">
                                <CardHeader className="bg-transparent border-0 pb-0">
                                    <div className="d-flex justify-content-between align-items-start flex-wrap gap-2">
                                        <div>
                                            <h5 className="mb-1">Reglements deja associes aux BL</h5>
                                            <div className="text-muted small">
                                                Les reglements eligibles seront rattaches a la facture lors de sa confirmation. Aucun nouveau reglement ne sera cree.
                                            </div>
                                        </div>
                                        <span className={`badge ${paymentPreview.remaining <= 0 ? 'bg-success' : paymentPreview.covered > 0 ? 'bg-warning text-dark' : 'bg-secondary'}`}>
                                            Statut attendu : {paymentPreview.status}
                                        </span>
                                    </div>
                                </CardHeader>
                                <CardBody>
                                    <div className="table-responsive">
                                        <Table borderless size="sm" className="align-middle mb-3">
                                            <thead>
                                                <tr>
                                                    <th>BL source</th>
                                                    <th className="text-end">Inclus dans la facture</th>
                                                    <th className="text-end">Disponible sur le BL</th>
                                                    <th className="text-end">Transfert propose</th>
                                                    <th className="text-end">Reliquat BL</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {paymentPreview.rows.map(row => (
                                                    <tr key={row.id}>
                                                        <td><strong>{row.numero}</strong><div className="small text-muted">{row.dateLivraison || '-'}</div></td>
                                                        <td className="text-end">{formatPrice(row.contribution)}</td>
                                                        <td className="text-end">{formatPrice(row.available)}</td>
                                                        <td className="text-end fw-bold text-primary">{formatPrice(row.proposed)}</td>
                                                        <td className="text-end">{formatPrice(Math.max(0, row.available - row.proposed))}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </Table>
                                    </div>
                                    <Row className="g-2">
                                        <Col xs={6} md={3}><div className="bg-white rounded p-2 h-100"><small className="text-muted d-block">Total facture</small><strong>{formatPrice(values.montantTotalTTC || 0)}</strong></div></Col>
                                        <Col xs={6} md={3}><div className="bg-white rounded p-2 h-100"><small className="text-muted d-block">Montant couvert</small><strong className="text-success">{formatPrice(paymentPreview.covered)}</strong></div></Col>
                                        <Col xs={6} md={3}><div className="bg-white rounded p-2 h-100"><small className="text-muted d-block">Reste a payer</small><strong className={paymentPreview.remaining > 0 ? 'text-danger' : 'text-success'}>{formatPrice(paymentPreview.remaining)}</strong></div></Col>
                                        <Col xs={6} md={3}><div className="bg-white rounded p-2 h-100"><small className="text-muted d-block">Reliquat disponible</small><strong>{formatPrice(paymentPreview.unused)}</strong></div></Col>
                                    </Row>
                                </CardBody>
                            </Card>
                        )}
                        <div className="d-flex justify-content-between align-items-center border-top pt-3 mb-2"><div><h5 className="mb-1">Lignes à facturer</h5><div className="small text-muted">Seules les quantités encore disponibles seront acceptées.</div></div><span className="badge bg-primary">{values.lignes?.length || 0} ligne(s)</span></div>
                        <Row>
                            <CommandeLine formik={formik} />
                        </Row>
                        {errors.lignes && touched.lignes && (
                            <div className="text-danger small mt-1">{errors.lignes}</div>
                        )}
                        <Row className="mt-3 justify-content-end">
                            <Col md="4" className="text-end">
                                <div className="mb-2">
                                    <h5>{t('devis.totalHT')}: {formatPrice(montantTotalHT)}</h5>
                                </div>
                                <div className="mb-2">
                                    <h5>{t('devis.totalTaxe')}: {formatPrice(montantTotalTaxe)}</h5>
                                </div>
                                <div>
                                    <h5>{t('devis.totalTTC')}: {formatPrice(montantTotalTTC)}</h5>
                                </div>
                            </Col>
                        </Row>
                        {!isEditing && (
                            <Card className="border-0 bg-light mb-3"><CardHeader className="bg-transparent"><strong>Avances client disponibles</strong></CardHeader><CardBody>
                                {loadingAdvances ? <div className="text-center"><Spinner size="sm" /> Chargement...</div>
                                    : availableAdvances.length === 0 ? <div className="text-muted">Aucune avance disponible pour ce client et cette entreprise.</div>
                                    : <Table borderless size="sm" responsive className="align-middle mb-0"><tbody>{availableAdvances.map(advance => <tr key={advance.id}>
                                        <td style={{ width: 36 }}><Input type="checkbox" checked={selectedAdvanceIds.includes(advance.id)} onChange={event => setSelectedAdvanceIds(current => event.target.checked ? [...new Set([...current, advance.id])] : current.filter(id => id !== advance.id))} /></td>
                                        <td><strong>{advance.referenceReglement || `Avance #${advance.id}`}</strong><div className="small text-muted">{advance.dateCreation || '-'}</div></td>
                                        <td className="text-end text-success fw-bold">{formatPrice(advance.montantDisponible || 0)}</td>
                                    </tr>)}</tbody></Table>}
                            </CardBody></Card>
                        )}
                        <Card className="border-0 bg-dark text-white mt-3" style={{ position: 'sticky', bottom: 0, zIndex: 5 }}><CardBody><Row className="g-2 align-items-center">
                            <Col><small className="text-white-50 d-block">Total TTC</small><strong className="fs-5">{formatPrice(montantTotalTTC)}</strong></Col>
                            <Col><small className="text-white-50 d-block">Paiements BL</small><strong>{formatPrice(paymentPreview.covered)}</strong></Col>
                            <Col><small className="text-white-50 d-block">Avances</small><strong>{formatPrice(paymentPreview.advanceCovered)}</strong></Col>
                            <Col><small className="text-white-50 d-block">Reste attendu</small><strong>{formatPrice(paymentPreview.remaining)}</strong></Col>
                            <Col><span className={`badge ${paymentPreview.remaining <= 0 ? 'bg-success' : paymentPreview.covered + paymentPreview.advanceCovered > 0 ? 'bg-warning text-dark' : 'bg-secondary'}`}>{paymentPreview.status}</span></Col>
                        </Row></CardBody></Card>
                        <div className="mt-4 d-flex justify-content-between">
                            <Button color="secondary" onClick={onClose} className="me-2">
                                {t('common.cancel')}
                            </Button>
                            <div className="d-flex gap-2">
                            {!isEditing && <Button type="button" color="secondary" disabled={isLoading} onClick={saveDraft}>Enregistrer le brouillon</Button>}
                            <Button color="success" type="submit" disabled={isLoading}>
                                {isLoading
                                    ? (isEditing ? 'Enregistrement des modifications…' : 'Création de la facture et mise à jour des règlements liés…')
                                    : (isEditing ? 'Enregistrer les modifications' : 'Créer et confirmer la facture')}
                            </Button>
                            </div>
                        </div>
                    </CardBody>
                </Card>
            </Form>

            <Modal isOpen={showEcheanceModal} toggle={() => setShowEcheanceModal(false)} size="lg">
                <ModalHeader toggle={() => setShowEcheanceModal(false)} className="bg-warning bg-opacity-10 border-0">
                    <FaCalendarAlt className="me-2 text-warning" />
                    <span className="fw-bold text-warning">{t('client.echeancesDetails')}</span>
                </ModalHeader>
                <ModalBody>
                    {echeances && echeances.length > 0 ? (
                        <div>
                            <div className="mb-3 d-flex justify-content-between align-items-center">
                                <span className="fw-bold text-warning fs-5">
                                    <FaCalendarAlt className="me-2" /> {t('client.echeances')}: {echeances.length}
                                </span>
                                <span className="fw-bold text-muted">{t('echeance.total')}: <span className="text-warning">{formatPrice(getTotalMontant(echeances))}</span></span>
                            </div>
                            <Table bordered hover responsive className="table-sm">
                                <thead className="bg-warning bg-opacity-10">
                                    <tr>
                                        <th>{t('echeance.reference')}</th>
                                        <th>{t('echeance.dateReglement')}</th>
                                        <th>{t('echeance.numeroCheque')}</th>
                                        <th>{t('echeance.banque')}</th>
                                        <th>{t('echeance.montantCheque')}</th>
                                        <th>{t('echeance.dateEcheance')}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {echeances.map((echeance, idx) => (
                                        <tr key={idx}>
                                            <td>{echeance.reference}</td>
                                            <td>{echeance.dateReglement}</td>
                                            <td>{echeance.numeroCheque}</td>
                                            <td>{echeance.banque}</td>
                                            <td className="text-end fw-bold text-warning">{formatPrice(echeance.montantRegle)}</td>
                                            <td>{echeance.dateEcheance}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        </div>
                    ) : (
                        <div className="text-muted text-center py-4">{t('client.noEcheances')}</div>
                    )}
                </ModalBody>
            </Modal>

            <Modal isOpen={showCautionModal} toggle={() => setShowCautionModal(false)} size="lg">
                <ModalHeader toggle={() => setShowCautionModal(false)} className="bg-info bg-opacity-10 border-0">
                    <FaShieldAlt className="me-2 text-info" />
                    <span className="fw-bold text-info">{t('client.cautionsDetails')}</span>
                </ModalHeader>
                <ModalBody>
                    {cautions && cautions.length > 0 ? (
                        <div>
                            <div className="mb-3 d-flex justify-content-between align-items-center">
                                <span className="fw-bold text-info fs-5">
                                    <FaShieldAlt className="me-2" /> {t('client.cautions')}: {cautions.length}
                                </span>
                                <span className="fw-bold text-muted">{t('caution.total')}: <span className="text-info">{formatPrice(getTotalMontant(cautions))}</span></span>
                            </div>
                            <Table bordered hover responsive className="table-sm">
                                <thead className="bg-info bg-opacity-10">
                                    <tr>
                                        <th>{t('caution.reference')}</th>
                                        <th>{t('caution.dateReglement')}</th>
                                        <th>{t('caution.numeroCheque')}</th>
                                        <th>{t('caution.banque')}</th>
                                        <th>{t('caution.montantCheque')}</th>
                                        <th>{t('caution.dateEcheance')}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {cautions.map((caution, idx) => (
                                        <tr key={idx}>
                                            <td>{caution.reference}</td>
                                            <td>{caution.dateReglement}</td>
                                            <td>{caution.numeroCheque}</td>
                                            <td>{caution.banque}</td>
                                            <td className="text-end fw-bold text-info">{formatPrice(caution.montantRegle)}</td>
                                            <td>{caution.dateEcheance}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        </div>
                    ) : (
                        <div className="text-muted text-center py-4">{t('client.noCautions')}</div>
                    )}
                </ModalBody>
            </Modal>
        </Container>
    );
};

export default InvoiceForm;
